import { BottomDrawer } from 'components/molecules/BottomDrawer';
import Chip from 'components/Chip/Chip';
import ChipList from 'components/ChipList';
import { ResponsiveFlex } from 'components/common/styled/common-styled';
import { Modal } from 'components/Modal';
import ResourceGroupIncludedItems from 'components/Resource/ResourceGroupIncludedItems';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { TruncatedText, TruncationMode } from 'components/TruncatedText';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useIsMobile } from 'WindowDimensionProvider';
import { PromotersCard } from 'components/molecules/PromotersCard';
import { Brand } from 'api/brand/fetchBrandById';

const getCapacityLabelFromCapacity = (
  capacity: number | null,
  translate: (s: string) => string,
) => {
  const displayedCapacity = Number(capacity);
  return displayedCapacity > 0
    ? `${displayedCapacity} ${translate(
        displayedCapacity === 1 ? 'person' : 'people',
      )}`
    : '';
};

interface FullDetailProps {
  cancellationPolicyText?: string;
  capacityCount: number;
  customStyles?: ReturnType<typeof css>;
  description: string;
  includedItems: string[];
  maxLines: number;
  vibes?: string[];
}

const FullDetail = ({
  cancellationPolicyText,
  capacityCount,
  customStyles,
  description,
  includedItems = [],
  maxLines,
  vibes,
}: FullDetailProps) => {
  const { t: translate } = useTranslation();
  const capacityLabel = getCapacityLabelFromCapacity(capacityCount, translate);

  return (
    <ResponsiveFlex direction="column" gap={36} alignItem="flex-start">
      <ResponsiveFlex direction="column" gap={16} alignItem="flex-start">
        <ChipList>
          {vibes?.map((vibe) => (
            <Chip key={vibe} className="listing-card-vibe">
              {vibe}
            </Chip>
          ))}
        </ChipList>
        <TruncatedText
          customStyles={customStyles}
          mode={TruncationMode.INLINE}
          text={description}
          maxLines={maxLines}
          showLessText={translate('viewLess')}
          showMoreText={translate('viewMore')}
          showMoreAriaLabel={translate('viewFullDescription')}
          isRichText
        />
      </ResponsiveFlex>

      {includedItems.length > 0 && (
        <div>
          <ResourceGroupIncludedItems
            title={translate('included')}
            includedItems={includedItems}
            headingProps={{
              fontWeight: 700,
              fontSize: '14px',
            }}
          />
        </div>
      )}

      {capacityLabel && (
        <ResponsiveFlex direction="column" gap={20} alignItem="flex-start">
          <ResourceHeading2
            className="resource-group-included__heading"
            fontSize="14px"
            fontWeight="700"
          >
            {translate('capacity')}
          </ResourceHeading2>

          <ResourceParagraph className="capacity-label">
            {capacityLabel}
          </ResourceParagraph>
        </ResponsiveFlex>
      )}

      {cancellationPolicyText && (
        <ResponsiveFlex direction="column" gap={20} alignItem="flex-start">
          <ResourceHeading2
            className="resource-group-cancellation-policy__heading"
            fontSize="14px"
            fontWeight="700"
          >
            {translate('cancellationPolicy')}
          </ResourceHeading2>

          <ResourceParagraph className="cancellation-policy-label">
            {cancellationPolicyText}
          </ResourceParagraph>
        </ResponsiveFlex>
      )}
    </ResponsiveFlex>
  );
};

interface ResourceGroupFullDetailProps {
  cancellationPolicyText?: string;
  capacityCount: number;
  customStyles?: ReturnType<typeof css>;
  description: string;
  includedItems: string[];
  maxLines: number;
  onClose: () => void;
  showContent: boolean;
  vibes?: string[];
  promoterBrand?: Brand;
}

export const ResourceGroupFullDetail = ({
  cancellationPolicyText,
  capacityCount,
  customStyles,
  description,
  includedItems,
  maxLines,
  onClose,
  showContent,
  vibes,
  promoterBrand,
}: ResourceGroupFullDetailProps) => {
  const isMobileView = useIsMobile();
  const { t: translate } = useTranslation();

  return (
    <FullDetailContainer>
      {isMobileView ? (
        <BottomDrawer
          open={showContent}
          heading={translate('details')}
          size="full"
          onClose={onClose}
        >
          <FullDetail
            cancellationPolicyText={cancellationPolicyText}
            capacityCount={capacityCount}
            customStyles={customStyles}
            description={description}
            includedItems={includedItems}
            maxLines={maxLines}
            vibes={vibes}
          />
          {promoterBrand?.name && (
            <PromotersCard promoterBrand={promoterBrand} />
          )}
        </BottomDrawer>
      ) : (
        <Modal
          size="large"
          title={translate('details')}
          disableDismissOnClickOutside={false}
          isVisible={showContent}
          isClosable
          onClose={onClose}
          content={
            <FullDetail
              capacityCount={capacityCount}
              customStyles={customStyles}
              description={description}
              includedItems={includedItems}
              maxLines={maxLines}
              vibes={vibes}
              cancellationPolicyText={cancellationPolicyText}
            />
          }
        />
      )}
    </FullDetailContainer>
  );
};

const FullDetailContainer = styled.div`
  display: contents;
  .modal-overlay {
    background-color: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'var(--way-palette-black-30)'
        : 'var(--way-palette-black-10)'} !important;
  }
  .modal-content {
    box-shadow: var(--way-design-boxShadow-s);
    border: 0.5px solid var(--way-colors-borderColor);
    border-radius: 0;
    position: relative;
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'var(--way-palette-black-60)'
        : 'var(--way-palette-white-60)'};
    > * {
      z-index: +1;
      position: relative;
    }

    &:after {
      content: '';
      width: 100%;
      height: 100%;
      backdrop-filter: blur(120px) !important;
      position: absolute;
      inset: 0;
    }
  }

  .read-more-btn {
    color: var(--way-colors-contrastColorShades-100) !important;
  }

  .modal-title {
    font-weight: 700;
    font-size: 28px;
    line-height: 34px;
    color: var(--way-colors-contrastColorShades-100) !important;
  }
`;
